import React, { useMemo, useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Divider,
  Space,
  Tag,
  Select,
  Input,
  Button,
  Table,
  Alert,
  Progress,
  Tooltip,
  Badge,
  Spin,
  notification,
} from "antd";
import {
  ShoppingCartOutlined,
  UserOutlined,
  DollarOutlined,
  PercentageOutlined,
  CalendarOutlined,
  FilterOutlined,
  ClearOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  TrophyOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import ChartCard from "../../charts/ChartCard";
import { getStockOrdenesServicios } from "../../../services/entregas/servicesOrdenesCompra";
import * as XLSX from "xlsx";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Search } = Input;

const InformeIngresoPO = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estados para filtros
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchProduct, setSearchProduct] = useState("");
  const [showLowEfficiency, setShowLowEfficiency] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getStockOrdenesServicios();
      const { status, data: responseData } = response;

      if (status) {
        setData(responseData);
      } else {
        notification.error({ message: response.message });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while fetching data.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Datos filtrados
  const filteredData = useMemo(() => {
    let filtered = [...data];

    if (selectedSupplier) {
      filtered = filtered.filter(
        (item) => item.proveedor_nombre === selectedSupplier
      );
    }

    if (selectedOrder) {
      filtered = filtered.filter((item) => item.numero_orden === selectedOrder);
    }

    if (searchProduct) {
      filtered = filtered.filter((item) =>
        item.productoPO.toLowerCase().includes(searchProduct.toLowerCase())
      );
    }

    if (showLowEfficiency) {
      filtered = filtered.filter((item) => item.porcentaje_asignado < 100);
    }

    return filtered;
  }, [data, selectedSupplier, selectedOrder, searchProduct, showLowEfficiency]);

  // Listas únicas para filtros
  const uniqueSuppliers = [
    ...new Set(data.map((item) => item.proveedor_nombre)),
  ];
  const uniqueOrders = [...new Set(data.map((item) => item.numero_orden))];

  // Cálculos y análisis de datos
  const analytics = useMemo(() => {
    const totalOrders = filteredData.length;
    const totalValue = filteredData.reduce(
      (sum, item) => sum + item.valor_unitario * item.cantidad_po,
      0
    );
    const totalQuantity = filteredData.reduce(
      (sum, item) => sum + item.cantidad_po,
      0
    );
    const totalAssigned = filteredData.reduce(
      (sum, item) => sum + item.cantidad_asignada,
      0
    );
    const avgUnitValue = totalValue / totalQuantity || 0;
    const assignmentEfficiency =
      totalQuantity > 0 ? (totalAssigned / totalQuantity) * 100 : 0;

    const suppliers = [
      ...new Set(filteredData.map((item) => item.proveedor_nombre)),
    ];
    const lowEfficiencyProducts = filteredData.filter(
      (item) => item.porcentaje_asignado < 100
    );
    const highValueProducts = filteredData.filter(
      (item) => item.valor_unitario > 100000
    );

    return {
      totalOrders,
      totalValue,
      totalQuantity,
      totalAssigned,
      avgUnitValue,
      assignmentEfficiency,
      suppliers,
      lowEfficiencyProducts,
      highValueProducts,
    };
  }, [filteredData]);

  // Datos por proveedor
  const supplierAnalytics = useMemo(() => {
    const supplierData = {};

    filteredData.forEach((item) => {
      if (!supplierData[item.proveedor_nombre]) {
        supplierData[item.proveedor_nombre] = {
          name: item.proveedor_nombre,
          products: 0,
          totalValue: 0,
          totalQuantity: 0,
          avgEfficiency: 0,
          orders: new Set(),
        };
      }

      supplierData[item.proveedor_nombre].products += 1;
      supplierData[item.proveedor_nombre].totalValue +=
        item.valor_unitario * item.cantidad_po;
      supplierData[item.proveedor_nombre].totalQuantity += item.cantidad_po;
      supplierData[item.proveedor_nombre].orders.add(item.numero_orden);
    });

    // Calcular eficiencia promedio por proveedor
    Object.keys(supplierData).forEach((supplier) => {
      const supplierItems = filteredData.filter(
        (item) => item.proveedor_nombre === supplier
      );
      const totalEfficiency = supplierItems.reduce(
        (sum, item) => sum + item.porcentaje_asignado,
        0
      );
      supplierData[supplier].avgEfficiency =
        totalEfficiency / supplierItems.length;
      supplierData[supplier].orders = supplierData[supplier].orders.size;
    });

    return Object.values(supplierData);
  }, [filteredData]);

  // Datos para gráfico de proveedores - Cantidad de productos
  const supplierProductCountData = useMemo(
    () => ({
      labels: supplierAnalytics.map((s) =>
        s.name.length > 15 ? s.name.substring(0, 15) + "..." : s.name
      ),
      datasets: [
        {
          label: "Cantidad de Productos",
          data: supplierAnalytics.map((s) => s.products),
          backgroundColor: "rgba(54, 162, 235, 0.8)",
          borderColor: "rgba(54, 162, 235, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [supplierAnalytics]
  );

  // Datos para gráfico de proveedores - Valor total
  const supplierValueData = useMemo(
    () => ({
      labels: supplierAnalytics.map((s) =>
        s.name.length > 15 ? s.name.substring(0, 15) + "..." : s.name
      ),
      datasets: [
        {
          label: "Valor Total ($)",
          data: supplierAnalytics.map((s) => s.totalValue),
          backgroundColor: "rgba(75, 192, 192, 0.8)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [supplierAnalytics]
  );

  // Datos para gráfico de eficiencia por producto
  const efficiencyData = useMemo(
    () => ({
      labels: filteredData.map((item) =>
        item.productoPO.split(",")[0].length > 20
          ? item.productoPO.split(",")[0].substring(0, 20) + "..."
          : item.productoPO.split(",")[0]
      ),
      datasets: [
        {
          label: "Porcentaje de asignación (%)",
          data: filteredData.map((item) => item.porcentaje_asignado),
          backgroundColor: filteredData.map((item) =>
            item.porcentaje_asignado === 100
              ? "rgba(82, 196, 26, 0.8)"
              : item.porcentaje_asignado >= 75
              ? "rgba(250, 173, 20, 0.8)"
              : "rgba(255, 99, 132, 0.8)"
          ),
          borderColor: filteredData.map((item) =>
            item.porcentaje_asignado === 100
              ? "rgba(82, 196, 26, 1)"
              : item.porcentaje_asignado >= 75
              ? "rgba(250, 173, 20, 1)"
              : "rgba(255, 99, 132, 1)"
          ),
          borderWidth: 2,
        },
      ],
    }),
    [filteredData]
  );

  // Datos para gráfico comparativo de valores unitarios
  const unitValueComparisonData = useMemo(
    () => ({
      labels: filteredData.map((item) =>
        item.productoPO.split(",")[0].length > 15
          ? item.productoPO.split(",")[0].substring(0, 15) + "..."
          : item.productoPO.split(",")[0]
      ),
      datasets: [
        {
          label: "Valor Unitario ($)",
          data: filteredData.map((item) => item.valor_unitario),
          backgroundColor: "rgba(153, 102, 255, 0.8)",
          borderColor: "rgba(153, 102, 255, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [filteredData]
  );

  // Columnas para tabla detallada
  const detailTableColumns = [
    {
      title: "Producto",
      dataIndex: "productoPO",
      key: "productoPO",
      render: (text) => (
        <Tooltip title={text}>
          <Text>{text.length > 30 ? text.substring(0, 30) + "..." : text}</Text>
        </Tooltip>
      ),
      width: 200,
    },
    {
      title: "Proveedor",
      dataIndex: "proveedor_nombre",
      key: "proveedor_nombre",
      filters: uniqueSuppliers.map((supplier) => ({
        text: supplier,
        value: supplier,
      })),
      onFilter: (value, record) => record.proveedor_nombre === value,
    },
    {
      title: "N° Orden",
      dataIndex: "numero_orden",
      key: "numero_orden",
      filters: uniqueOrders.map((order) => ({ text: order, value: order })),
      onFilter: (value, record) => record.numero_orden === value,
    },
    {
      title: "Cantidad PO",
      dataIndex: "cantidad_po",
      key: "cantidad_po",
      sorter: (a, b) => a.cantidad_po - b.cantidad_po,
      render: (value) => <Text strong>{value.toLocaleString()}</Text>,
    },
    {
      title: "Cantidad Asignada",
      dataIndex: "cantidad_asignada",
      key: "cantidad_asignada",
      sorter: (a, b) => a.cantidad_asignada - b.cantidad_asignada,
      render: (value) => <Text>{value.toLocaleString()}</Text>,
    },
    {
      title: "Valor Unitario",
      dataIndex: "valor_unitario",
      key: "valor_unitario",
      sorter: (a, b) => a.valor_unitario - b.valor_unitario,
      render: (value) => <Text>{formatCurrency(value)}</Text>,
    },
    {
      title: "Valor Total",
      key: "valor_total",
      render: (_, record) => (
        <Text strong style={{ color: "#52c41a" }}>
          {formatCurrency(record.valor_unitario * record.cantidad_po)}
        </Text>
      ),
      sorter: (a, b) =>
        a.valor_unitario * a.cantidad_po - b.valor_unitario * b.cantidad_po,
    },
    {
      title: "Porcentaje Asignado",
      dataIndex: "porcentaje_asignado",
      key: "porcentaje_asignado",
      sorter: (a, b) => a.porcentaje_asignado - b.porcentaje_asignado,
      render: (value) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Progress
            percent={value}
            size="small"
            status={
              value === 100 ? "success" : value >= 75 ? "normal" : "exception"
            }
            style={{ minWidth: "100px" }}
          />
          {value === 100 ? (
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
          ) : value >= 75 ? (
            <ExclamationCircleOutlined style={{ color: "#faad14" }} />
          ) : (
            <WarningOutlined style={{ color: "#ff4d4f" }} />
          )}
        </div>
      ),
    },
  ];

  // Columnas para tabla de análisis por proveedor
  const supplierTableColumns = [
    {
      title: "Proveedor",
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Productos",
      dataIndex: "products",
      key: "products",
      sorter: (a, b) => a.products - b.products,
      render: (value) => (
        <Badge count={value} style={{ backgroundColor: "#1890ff" }}>
          <DollarOutlined style={{ fontSize: "20px" }} />
        </Badge>
      ),
    },
    {
      title: "Órdenes",
      dataIndex: "orders",
      key: "orders",
      sorter: (a, b) => a.orders - b.orders,
      render: (value) => (
        <Badge count={value} style={{ backgroundColor: "#52c41a" }}>
          <ShoppingCartOutlined style={{ fontSize: "20px" }} />
        </Badge>
      ),
    },
    {
      title: "Valor Total",
      dataIndex: "totalValue",
      align: "right",
      key: "totalValue",
      sorter: (a, b) => a.totalValue - b.totalValue,
      render: (value) => (
        <Text strong style={{ color: "#52c41a", fontSize: "16px" }}>
          {formatCurrency(value)}
        </Text>
      ),
    },
    {
      title: "Cantidad Total",
      align: "center",
      dataIndex: "totalQuantity",
      key: "totalQuantity",
      sorter: (a, b) => a.totalQuantity - b.totalQuantity,
      render: (value) => <Text>{value.toLocaleString()}</Text>,
    },
    {
      title: "Estado de entrega",
      dataIndex: "avgEfficiency",
      key: "avgEfficiency",
      sorter: (a, b) => a.avgEfficiency - b.avgEfficiency,
      render: (value) => {
        const formattedValue = value?.toFixed(2); // dos decimales
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Progress
              percent={parseFloat(formattedValue)}
              size="small"
              status={
                value === 100 ? "success" : value >= 75 ? "normal" : "exception"
              }
              style={{ minWidth: "100px" }}
            />
            {value >= 90 ? (
              <TrophyOutlined style={{ color: "#faad14" }} />
            ) : value >= 75 ? (
              <CheckCircleOutlined style={{ color: "#52c41a" }} />
            ) : (
              <WarningOutlined style={{ color: "#ff4d4f" }} />
            )}
            <span>{formattedValue}%</span>{" "}
            {/* aquí lo muestras con dos decimales */}
          </div>
        );
      },
    },
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const clearAllFilters = () => {
    setSelectedSupplier(null);
    setSelectedOrder(null);
    setSearchProduct("");
    setShowLowEfficiency(false);
  };

  const exportarExcel = () => {
    let columnsExport = [
      { title: "Número de orden", dataIndex: "numero_orden" },
      { title: "Fecha", dataIndex: "fecha_po" },
      { title: "Nombre proveedor", dataIndex: "proveedor_nombre" },
      {
        title: "Identificación proveedor",
        dataIndex: "proveedor_identificacion",
      },
      { title: "Dirección proveedor", dataIndex: "proveedor_direccion" },
      { title: "Télefono proveedor", dataIndex: "proveedor_telefono" },
      { title: "Producto", dataIndex: "productoPO" },
      { title: "Unidad de medida", dataIndex: "unidad_medida" },
      { title: "Valor unitario", dataIndex: "valor_unitario" },
      { title: "Cantidad de entrada", dataIndex: "cantidad_entrada" },
      { title: "Cantidad asignada", dataIndex: "cantidad_asignada" },
      { title: "Porcentaje asignado", dataIndex: "porcentaje_asignado" },
    ];

    // Crear hoja con datos
    const ws = XLSX.utils.json_to_sheet(
      data.map((row) => {
        const obj = {};
        columnsExport.forEach((col) => {
          let valor = row[col.dataIndex];

          // Si es fecha, convertir a objeto Date para que Excel lo reconozca
          if (col.dataIndex === "fecha_po" && valor) {
            valor = new Date(valor);
          }

          obj[col.title] = valor;
        });
        return obj;
      }),
      { skipHeader: true, origin: "A2", cellDates: true } // cellDates activa fechas reales
    );

    // Agregar cabeceras manualmente
    const headerRow = columnsExport.map((col) => col.title);
    XLSX.utils.sheet_add_aoa(ws, [headerRow], { origin: "A1" });

    // Dar formato de fecha a la columna "Fecha" (columna 1)
    data.forEach((_, idx) => {
      const cellRef = XLSX.utils.encode_cell({ r: idx + 1, c: 1 }); // fila +1 porque cabecera está en r=0
      if (ws[cellRef]) {
        ws[cellRef].t = "d"; // tipo fecha
        ws[cellRef].z = "yyyy-mm-dd"; // formato
      }
    });

    // Estilo para cabeceras
    headerRow.forEach((_, idx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: idx });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: { fgColor: { rgb: "FFC000" } },
        font: { bold: true, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" },
      };
    });

    // Autoajustar ancho de columnas
    const colWidths = headerRow.map((h, i) => {
      const valores = data.map((row) => {
        let v = row[columnsExport[i].dataIndex];
        if (v instanceof Date) v = v.toISOString().split("T")[0]; // para medir ancho
        return v ? v.toString() : "";
      });
      const maxLength = Math.max(h.length, ...valores.map((v) => v.length));
      return { wch: maxLength + 2 };
    });
    ws["!cols"] = colWidths;

    // Crear y exportar archivo
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Informe Espiga");
    XLSX.writeFile(wb, "informe_ingresdo_po.xlsx");
  };

  const exportarExcelDEtalleCompletoProductos = () => {
    let columnsExport = [
      { title: "Código", dataIndex: "codigo_sir" },
      { title: "Nombre del participante", dataIndex: "nombre_participante" },
      { title: "Genero", dataIndex: "genero" },
      { title: "Departamento", dataIndex: "departamento" },
      { title: "Municipio", dataIndex: "municipio" },
      { title: "Modulo", dataIndex: "nombre_modulo" },
      { title: "Total estaciones", dataIndex: "total_estaciones_modulo" },
      { title: "Estaciones completadas", dataIndex: "estaciones_completadas" },
      { title: "Porcentaje", dataIndex: "porcentaje_avance" },
    ];

    const ws = XLSX.utils.json_to_sheet(
      data.map((row) => {
        const obj = {};
        columnsExport.forEach((col) => {
          obj[col.title] = row[col.dataIndex];
        });
        return obj;
      }),
      { origin: "A2" } // Dejamos la fila 1 para cabeceras
    );

    // Agregar manualmente cabeceras en la fila 1
    const headerRow = columnsExport.map((col) => col.title);
    XLSX.utils.sheet_add_aoa(ws, [headerRow], { origin: "A1" });

    // Aplicar estilos a cabeceras (fila 1)
    headerRow.forEach((_, idx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: idx });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: {
          fgColor: { rgb: "FFC000" }, // Color de fondo (amarillo Excel)
        },
        font: {
          bold: true,
          color: { rgb: "FFFFFF" }, // Texto blanco
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
        },
      };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Informe Espiga");
    XLSX.writeFile(wb, "informe_espiga_general.xlsx");
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #eef0f7ff 0%, #e7e0eeff 100%)",
        }}
      >
        <Card style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px" }}>
            <Text>Cargando datos del informe...</Text>
          </div>
        </Card>
      </div>
    );
  }
  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      {/* Encabezado del Informe */}
      <Card style={{ marginBottom: "24px", borderRadius: "12px" }}>
        <div
          style={{
            textAlign: "center",
            marginBottom: "32px",
            background: "rgba(255, 255, 255, 0.95)",
            padding: "24px",
            borderRadius: "16px",
            backdropFilter: "blur(10px)",
          }}
        >
          <Title
            level={1}
            style={{
              color: "#0B3360",
              marginBottom: "8px",
              fontSize: "2.5rem",
              fontWeight: "700",
            }}
          >
            📊 Informe Avanzado de Órdenes de Compra
          </Title>
          <Title
            level={2}
            style={{
              color: "#4A90E2",
              marginBottom: "16px",
              fontSize: "1.8rem",
              fontWeight: "500",
            }}
          >
            Análisis detallado con filtros avanzados y métricas específicas
          </Title>
          <Space>
            <Button
              type="primary"
              onClick={exportarExcel}
              icon={<ExportOutlined />}
            >
              Exportar XLSX
            </Button>
          </Space>
        </div>
      </Card>

      {/* Panel de Filtros */}
      <Card style={{ marginBottom: "24px", borderRadius: "12px" }}>
        <Title level={4} style={{ marginBottom: "16px" }}>
          <FilterOutlined /> Filtros Avanzados
        </Title>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Text strong>Proveedor:</Text>
            <Select
              style={{ width: "100%", marginTop: "4px" }}
              showSearch
              optionFilterProp="children" // busca dentro del texto de las opciones
              placeholder="Seleccionar proveedor"
              allowClear
              value={selectedSupplier}
              onChange={setSelectedSupplier}
            >
              {uniqueSuppliers.map((supplier) => (
                <Option key={supplier} value={supplier}>
                  {supplier}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong>Número de Orden:</Text>
            <Select
              style={{ width: "100%", marginTop: "4px" }}
              showSearch
              optionFilterProp="children" // busca dentro del texto de las opciones
              placeholder="Seleccionar orden"
              allowClear
              value={selectedOrder}
              onChange={setSelectedOrder}
            >
              {uniqueOrders.map((order) => (
                <Option key={order} value={order}>
                  {order}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong>Buscar Producto:</Text>
            <Search
              style={{ marginTop: "4px" }}
              placeholder="Buscar por nombre..."
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong>Filtros Especiales:</Text>
            <div style={{ marginTop: "4px" }}>
              {/*  
              <Button
                type={showLowEfficiency ? "primary" : "default"}
                onClick={() => setShowLowEfficiency(!showLowEfficiency)}
                style={{ marginRight: "8px" }}
              >
                Baja Eficiencia
              </Button>
             */}
              <Button icon={<ClearOutlined />} onClick={clearAllFilters}>
                Limpiar
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Alertas y Notificaciones */}
      {analytics.lowEfficiencyProducts.length > 0 && (
        <Alert
          message="⚠️ Productos con Baja Eficiencia de Asignación"
          description={`${analytics.lowEfficiencyProducts.length} productos tienen menos del 100% de asignación. Revisar para optimizar la gestión.`}
          type="warning"
          showIcon
          style={{ marginBottom: "24px", borderRadius: "8px" }}
        />
      )}

      {analytics.highValueProducts.length > 0 && (
        <Alert
          message="💎 Productos de Alto Valor"
          description={`${analytics.highValueProducts.length} productos superan los $100,000 por unidad. Monitoreo especial recomendado.`}
          type="info"
          showIcon
          style={{ marginBottom: "24px", borderRadius: "8px" }}
        />
      )}

      {filteredData.length === 0 && (
        <Alert
          message="Sin Resultados"
          description="No se encontraron productos que coincidan con los filtros aplicados."
          type="warning"
          showIcon
          style={{ marginBottom: "24px", borderRadius: "8px" }}
        />
      )}

      {/* Métricas Principales */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: "12px", textAlign: "center" }}>
            <Statistic
              title="Productos Filtrados"
              value={analytics.totalOrders}
              prefix={<DollarOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{
                color: "#1890ff",
                fontSize: "24px",
                fontWeight: "bold",
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: "12px", textAlign: "center" }}>
            <Statistic
              title="Valor Total"
              value={formatCurrency(analytics.totalValue)}
              prefix={<DollarOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{
                color: "#52c41a",
                fontSize: "20px",
                fontWeight: "bold",
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: "12px", textAlign: "center" }}>
            <Statistic
              title="Proveedores Activos"
              value={analytics.suppliers.length}
              prefix={<UserOutlined style={{ color: "#fa8c16" }} />}
              valueStyle={{
                color: "#fa8c16",
                fontSize: "24px",
                fontWeight: "bold",
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: "12px", textAlign: "center" }}>
            <Statistic
              title="Porcentaje de Asignación"
              value={analytics.assignmentEfficiency.toFixed(1)}
              suffix="%"
              prefix={<PercentageOutlined style={{ color: "#722ed1" }} />}
              valueStyle={{
                color:
                  analytics.assignmentEfficiency >= 90
                    ? "#52c41a"
                    : analytics.assignmentEfficiency >= 75
                    ? "#faad14"
                    : "#ff4d4f",
                fontSize: "24px",
                fontWeight: "bold",
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Gráficos Específicos por Proveedor */}
      <Row gutter={[24, 24]} style={{ marginBottom: "24px" }}>
        <Col xs={24} lg={12}>
          <ChartCard
            title="Cantidad de Productos por Proveedor"
            data={supplierProductCountData}
            chartType="bar"
            showAsPercentage={false}
          />
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard
            title="Valor Total por Proveedor"
            data={supplierValueData}
            chartType="bar"
            showAsPercentage={false}
          />
        </Col>
      </Row>

      {/* Gráficos de Eficiencia y Valores */}
      <Row gutter={[24, 24]} style={{ marginBottom: "24px" }}>
        <Col xs={24} lg={12}>
          <ChartCard
            title="Eficiencia de Asignación por Producto"
            data={efficiencyData}
            chartType="bar"
            showAsPercentage={false}
          />
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard
            title="Comparativo de Valores Unitarios"
            data={unitValueComparisonData}
            chartType="bar"
            showAsPercentage={false}
          />
        </Col>
      </Row>

      {/* Tabla de Análisis por Proveedor */}
      <Card style={{ marginBottom: "24px", borderRadius: "12px" }}>
        <Title level={3} style={{ color: "#0B3360", marginBottom: "16px" }}>
          📈 Análisis Detallado por Proveedor
        </Title>
        <Table
          columns={supplierTableColumns}
          dataSource={supplierAnalytics}
          rowKey="name"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1000 }}
          size="middle"
        />
      </Card>

      {/* Tabla Detallada de Productos */}
      <Card style={{ marginBottom: "24px", borderRadius: "12px" }}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: "16px" }}
        >
          <Col>
            <Title level={3} style={{ color: "#0B3360", margin: 0 }}>
              🔍 Detalle Completo de Productos
            </Title>
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={exportarExcelDEtalleCompletoProductos}
              icon={<ExportOutlined />}
              style={{
                backgroundColor: "#0B3360",
                borderColor: "#0B3360",
                borderRadius: "8px",
              }}
            >
              Exportar XLSX
            </Button>
          </Col>
        </Row>

        <Table
          columns={detailTableColumns}
          dataSource={filteredData}
          rowKey="oidStockLote"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          scroll={{ x: 1200 }}
          size="middle"
          summary={(pageData) => {
            const totalValue = pageData.reduce(
              (sum, item) => sum + item.valor_unitario * item.cantidad_po,
              0
            );
            const totalQuantity = pageData.reduce(
              (sum, item) => sum + item.cantidad_po,
              0
            );
            const avgEfficiency =
              pageData.reduce(
                (sum, item) => sum + item.porcentaje_asignado,
                0
              ) / pageData.length;

            return (
              <Table.Summary.Row
                style={{ backgroundColor: "#fafafa", fontWeight: "bold" }}
              >
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>TOTALES ({pageData.length} productos)</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <Text strong style={{ color: "#1890ff" }}>
                    {totalQuantity.toLocaleString()}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>-</Table.Summary.Cell>
                <Table.Summary.Cell index={5}>-</Table.Summary.Cell>
                <Table.Summary.Cell index={6}>
                  <Text strong style={{ color: "#52c41a" }}>
                    {formatCurrency(totalValue)}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7}>
                  <Progress
                    percent={avgEfficiency || 0}
                    size="small"
                    status={
                      avgEfficiency === 100
                        ? "success"
                        : avgEfficiency >= 75
                        ? "normal"
                        : "exception"
                    }
                  />
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Card>

      {/* Footer con Información Adicional */}
      <Card
        style={{
          marginBottom: "24px",
          textAlign: "center",
          borderRadius: "12px",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Space direction="vertical" size="small">
          <Text type="secondary" style={{ fontSize: "14px" }}>
            📋 Informe generado automáticamente •{" "}
            {new Date().toLocaleDateString("es-CO", {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Datos analizados: {data.length} registros • Filtros aplicados:{" "}
            {filteredData.length} registros mostrados
          </Text>
        </Space>
      </Card>
    </div>
  );
};

export default InformeIngresoPO;
